import {useHydrationDone} from '@aboutyou/lazy-imports';
import {useTimedOut} from '@aboutyou/ui-components';
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {DataFetcherRequest} from './useRequest';

export type PriorityConfig = {renderingPriority?: number};

interface RenderingsPrioritizerContext {
  renderingPriorities: Set<number>;
  registeredPriorities: React.MutableRefObject<Set<number>>;
  ignoreRenderingPriority: boolean;
  showLoadingViewOnFirstRenderForPrioritiesUpTo: number;

  addToQueue: (renderingPriority: number) => void;
  removeFromQueue: (renderingPriority: number) => void;
}

const RenderingPrioritizerContext = createContext<RenderingsPrioritizerContext>(
  {
    registeredPriorities: {current: new Set([])},
    renderingPriorities: new Set([]),
    ignoreRenderingPriority: true,
    showLoadingViewOnFirstRenderForPrioritiesUpTo: 0,
    addToQueue: () => {
      throw new Error(
        'addToQueue default value should never be. Please wrap you component in a RenderingPrioritizerBoundary',
      );
    },
    removeFromQueue: () => {
      throw new Error(
        'removeFromQueue default value should never be. Please wrap you component in a RenderingPrioritizerBoundary',
      );
    },
  },
);

const useRenderingsPrioritizerContext = (): RenderingsPrioritizerContext =>
  useContext(RenderingPrioritizerContext);

const useAddToRenderingQueue = () => {
  const {renderingPriorities, addToQueue} = useRenderingsPrioritizerContext();

  return (config: PriorityConfig) => {
    if (
      config.renderingPriority &&
      !renderingPriorities.has(config.renderingPriority)
    ) {
      addToQueue(config.renderingPriority);
    }
  };
};

const useRemoveFromRenderingQueue = () => {
  const {renderingPriorities, removeFromQueue} =
    useRenderingsPrioritizerContext();

  return (config: PriorityConfig) => {
    if (
      config.renderingPriority &&
      renderingPriorities.has(config.renderingPriority)
    ) {
      removeFromQueue(config.renderingPriority);
    }
  };
};

const useGetCanDisplayLoading = ({
  renderingPriority,
}: {
  renderingPriority: PriorityConfig['renderingPriority'];
}) => {
  const {
    registeredPriorities,
    ignoreRenderingPriority,
    renderingPriorities,
    showLoadingViewOnFirstRenderForPrioritiesUpTo,
  } = useRenderingsPrioritizerContext();

  return () => {
    if (!renderingPriority) {
      return true;
    }

    if (renderingPriority <= showLoadingViewOnFirstRenderForPrioritiesUpTo) {
      return true;
    }

    if (ignoreRenderingPriority) {
      // find the index of the previous element that was registered for this render boundary
      let prevIndex = renderingPriority - 1;
      while (!registeredPriorities.current.has(prevIndex) && prevIndex > 0) {
        prevIndex = prevIndex - 1;
      }
      // if time is elapsed only show loading view if the previous element is done
      const previousRenderingPriorityIsDone =
        !renderingPriorities.has(prevIndex);

      return previousRenderingPriorityIsDone;
    }

    // lower priority number is higher priority, ie: 1 has more priority than 2
    const highestPriority = Math.min(...renderingPriorities.values());
    return renderingPriority <= highestPriority;
  };
};

const useGetCanDisplayDataView = (config: {
  renderingPriority: PriorityConfig['renderingPriority'];
}) => {
  const renderingPriority = config.renderingPriority;
  const {ignoreRenderingPriority, renderingPriorities} =
    useRenderingsPrioritizerContext();

  return () => {
    if (ignoreRenderingPriority || !renderingPriority) {
      return true;
    }
    const pendingRenderingPriorities = [...renderingPriorities.values()];

    const hasAnyHigherPriorityRenderingPending =
      pendingRenderingPriorities.some(
        // lower priority number is higher priority, ie: 1 has more priority than 2
        priority => priority < renderingPriority,
      );

    return !hasAnyHigherPriorityRenderingPending;
  };
};

export const useRenderingPrioritization = <T extends object>({
  renderingPriority,
  request,
}: {
  renderingPriority: PriorityConfig['renderingPriority'];
  request: DataFetcherRequest<T, unknown>;
}) => {
  const addToRenderingQueue = useAddToRenderingQueue();
  const removeFromRenderingQueue = useRemoveFromRenderingQueue();

  const getCanDisplayDataView = useGetCanDisplayDataView({
    renderingPriority,
  });

  const displayClientData = request.data && getCanDisplayDataView();

  useEffect(() => {
    if (renderingPriority && request.isLoading) {
      addToRenderingQueue({renderingPriority});
    }
  }, [renderingPriority, request.isLoading]);

  useEffect(() => {
    if (renderingPriority && displayClientData) {
      removeFromRenderingQueue({renderingPriority});
    }
  }, [renderingPriority, displayClientData]);

  useEffect(() => {
    if (renderingPriority && request.error) {
      removeFromRenderingQueue({renderingPriority});
    }
  }, [renderingPriority, request.error]);
};

export const useShouldShowDataView = <T extends object>({
  renderingPriority,
  request,
}: {
  renderingPriority: PriorityConfig['renderingPriority'];
  request: DataFetcherRequest<T, unknown>;
}) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const getCanDisplayDataView = useGetCanDisplayDataView({
    renderingPriority,
  });

  return !!request.data && getCanDisplayDataView();
};

export const useShouldLoadingView = <T extends object>({
  renderingPriority,
  request,
  disableLoadingInIdleState,
}: {
  renderingPriority: PriorityConfig['renderingPriority'];
  request: DataFetcherRequest<T, unknown>;
  disableLoadingInIdleState?: boolean;
}) => {
  const getCanDisplayLoading = useGetCanDisplayLoading({renderingPriority});

  if (!disableLoadingInIdleState && request.isIdle) {
    return true;
  }

  // cover the case where the request is configured as `keepPreviousData: 'keep-until-frustrating'` and the loading spinner should show, but technically priority is not high enough. As we already rendered components on the page and they should not unmount if the page comes in loading or previous data data mode we need to also show the loading state in this case
  if (!request.data && request.isPlaceholderData) {
    return true;
  }

  return renderingPriority ? getCanDisplayLoading() : request.isLoading;
};

export const RenderingPrioritizerBoundary: React.FC<{
  ignorePriorityAfterDuration?: number;
  showLoadingViewOnFirstRenderForPrioritiesUpTo?: number;
  children:
    | React.ReactNode
    | React.ReactElement[]
    | ((options: {
        completedAllRenderings: boolean;
        canRenderElementsForPriority: (renderingPriority: number) => boolean;
      }) => React.ReactElement);
}> = ({
  children,
  ignorePriorityAfterDuration = 100,
  showLoadingViewOnFirstRenderForPrioritiesUpTo = 1,
}) => {
  const isHydrationDone = useHydrationDone();
  const [startedRendering, setStartedRendering] = useState(false);
  const [renderingPriorities, setRenderingPriorities] = useState(
    new Set<number>(),
  );
  const registeredPriorities = useRef(new Set<number>());
  const ignoreRenderingPriority =
    // if all rendering priorities are removed, then we should avoid changing the value ignoreRenderingPriority to avoid a re-render
    useTimedOut(ignorePriorityAfterDuration) && renderingPriorities.size > 0;

  const value = React.useMemo(() => {
    return {
      registeredPriorities,
      renderingPriorities,
      addToQueue(renderingPriority: number) {
        registeredPriorities.current.add(renderingPriority);
        if (!renderingPriorities.has(renderingPriority)) {
          const newPriorities = renderingPriorities.add(renderingPriority);
          setRenderingPriorities(new Set(newPriorities));
          if (!startedRendering) {
            setStartedRendering(true);
          }
        }
      },
      removeFromQueue(renderingPriority: number) {
        if (renderingPriorities.has(renderingPriority)) {
          const newPriorities = new Set<number>(renderingPriorities);
          newPriorities.delete(renderingPriority);
          setRenderingPriorities(newPriorities);
        }
      },
      showLoadingViewOnFirstRenderForPrioritiesUpTo,
      ignoreRenderingPriority,
      completedAllRenderings:
        startedRendering && renderingPriorities.size === 0,
    };
  }, [
    registeredPriorities,
    renderingPriorities,
    ignoreRenderingPriority,
    startedRendering,
    showLoadingViewOnFirstRenderForPrioritiesUpTo,
  ]);

  const completedAllRenderings =
    startedRendering && renderingPriorities.size === 0;

  const canRenderElementsForPriority = (renderingPriority: number) => {
    if (!isHydrationDone || ignoreRenderingPriority) {
      return true;
    }
    return startedRendering && !renderingPriorities.has(renderingPriority);
  };

  return (
    <RenderingPrioritizerContext.Provider value={value}>
      {typeof children === 'function'
        ? children({
            completedAllRenderings,
            canRenderElementsForPriority,
          })
        : children}
    </RenderingPrioritizerContext.Provider>
  );
};
